import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { type PackagingInformationRefinedEntryDTO, type WarehouseStorageDataDTO } from "../../dto/ProductDataDTO";
import {
  type InStockEntryData,
  type InStockOrProductionEntryDataInCW,
  type PackagingInformationEntry,
  type WarehouseStockData,
} from "../../models/ProductData";
import { getPackageCode } from "../../utils/cartHelper";
import { showDateAsCalendarWeek } from "~/utils/features";
import instance from "~/utils/i18n";
import { groupByFn, isNonNull } from "~/utils/reducers";

dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

export interface AvailabilityInfoDTO {
  code: string;
  sapMaterialNumber: string;
  packagingInformationRefined: PackagingInformationRefinedEntryDTO[] | null;
  isFreshPnaCall: boolean | null;
}

export interface AvailabilityInfo {
  code: string;
  sapMaterialNumber: string;

  hasPackaging: boolean;
  packagingInformation: PackagingInformationEntry[];
  isFreshPnaCall: boolean;
}

export function transformAvailability(dto: AvailabilityInfoDTO): AvailabilityInfo {
  const packagingInfo = (dto.packagingInformationRefined || []).map((entry) => {
    const global = transformWarehouse(entry.global);
    const warehouses = entry.warehouses.map(transformWarehouse);
    return {
      packCode: getPackageCode(entry.code),
      packSize: entry.packsize,
      minOrderQuantity: entry.minOrderQuantity ? entry.minOrderQuantity : 1,
      primaryWarehouseCode: entry.primaryWarehouseCode,
      primaryWarehouseName: entry.primaryWarehouseName,
      replenishmentLeadTimeDate: entry.replenishmentLeadTimeDate ? new Date(entry.replenishmentLeadTimeDate) : null,
      preferredPackQuantity: entry.prefPackQuantity ?? 0,
      global,
      warehouses,
      totalWarehouse: combineWarehouses([...warehouses, global]),
      salesStock: entry.salesStock,
    };
  }) as PackagingInformationEntry[];

  packagingInfo.sort((a, b) => a.packSize - b.packSize);

  return {
    code: dto.code,
    sapMaterialNumber: dto.sapMaterialNumber,

    hasPackaging: packagingInfo.length > 0,
    packagingInformation: packagingInfo, //
    isFreshPnaCall: dto.isFreshPnaCall || false,
  };
}

const transformWarehouse = (dto: WarehouseStorageDataDTO): WarehouseStockData => {
  const inStock: InStockEntryData[] = [];
  const inStockWeekly: InStockOrProductionEntryDataInCW[] = [];
  let inProductionWeekly: InStockOrProductionEntryDataInCW[] = [];

  const inProductionList = showDateAsCalendarWeek() ? dto.inProductionWeekly ?? [] : dto.inProduction ?? [];
  const entryWithoutDate = (inProductionList ?? []).filter((entry) => !entry.date)[0];
  if (entryWithoutDate) {
    inStock.push({
      quantity: entryWithoutDate.amount ?? 0,
    });
  }

  if (dto.inStock) {
    const list = Array.isArray(dto.inStock) ? dto.inStock : [dto.inStock];
    list.forEach((inStockItem) => {
      inStock.push({
        quantity: inStockItem.amount ?? 0,
        date: inStockItem.date ? new Date(inStockItem.date) : undefined,
      });
    });
  }

  if (dto.inStockWeekly) {
    const list = Array.isArray(dto.inStockWeekly) ? dto.inStockWeekly : [dto.inStockWeekly];
    list.forEach((inStockItem) => {
      inStockWeekly.push({
        quantity: inStockItem.amount ?? 0,
        date: inStockItem.date ? new Date(inStockItem.date) : undefined,
        cwDate: inStockItem.cwDate,
      });
    });
  }

  if (dto.inProductionWeekly) {
    inProductionWeekly = (dto.inProductionWeekly ?? [])
      .map((entry, index) => {
        if (!entry.date) {
          if (import.meta.env.DEV) {
            console.warn(`CW missing in entry nr ${index + 1} from warehouse ${dto.name ?? "global"} `);
          }
          return null;
        }
        return {
          quantity: entry.amount,
          cwDate: entry.cwDate,
          date: new Date(entry.date),
        };
      })
      .filter(isNonNull);
  }

  return {
    code: dto.code ?? "",
    name: dto.name ?? "",
    inStock,
    inStockWeekly,
    inProductionWeekly,
    inClarification: dto.inClarification ?? 0,
    inProduction: (dto.inProduction ?? [])
      .map((entry, index) => {
        if (!entry.date) {
          if (import.meta.env.DEV) {
            console.warn(`Date missing in entry nr ${index + 1} from warehouse ${dto.name ?? "global"} `);
          }
          return null;
        }
        return {
          quantity: entry.amount,
          date: new Date(entry.date),
        };
      })
      .filter(isNonNull),
  };
};

function combineWarehouses(warehouses: WarehouseStockData[]): WarehouseStockData {
  const primaryWarehouse = warehouses[0];

  return {
    ...primaryWarehouse,
    inStock: combineStockEntries(warehouses.flatMap((warehouse) => warehouse.inStock)),
    inProduction: combineStockEntries(warehouses.flatMap((warehouse) => warehouse.inProduction)),
    inClarification: warehouses.reduce((acc, warehouse) => acc + warehouse.inClarification, 0),
    inStockWeekly: combineStockEntriesForCWData(
      warehouses.flatMap((warehouse) => warehouse.inStockWeekly ?? []).filter(Boolean),
    ),
    inProductionWeekly: combineStockEntriesForCWData(
      warehouses.flatMap((warehouse) => warehouse.inProductionWeekly ?? []).filter(Boolean),
    ),
  };
}

function combineStockEntries<T extends InStockEntryData>(stockEntries: T[]): T[] {
  const grouped = groupByFn(stockEntries, (entry) => dayjs(entry.date).format("YYYY-MM-DD"));
  return Object.values(grouped)
    .map((sameDayEntries: T[]) => ({
      date: sameDayEntries[0].date,
      quantity: sameDayEntries.reduce((acc, entry) => acc + entry.quantity, 0),
    }))
    .sort((e1, e2) => {
      if (e1.date === undefined) {
        return -1;
      }
      if (e2.date === undefined) {
        return -1;
      }
      return e1.date.getTime() - e2.date.getTime();
    }) as T[];
}

function combineStockEntriesForCWData<T extends InStockOrProductionEntryDataInCW>(stockEntries: T[]): T[] {
  const grouped = groupByFn(stockEntries, (entry) => entry.cwDate);

  const parseDate = (dateStr: string): dayjs.Dayjs => {
    // Parse "CW41/2025" -> week and year
    const regex = new RegExp(`^${instance.t("feature.shipdate.to.iso.week")}(\\d{1,2})\\/(\\d{4})$`);
    const match = dateStr.match(regex);
    if (!match) {
      throw new Error(`Invalid CW date format: ${dateStr}`);
    }
    const week = parseInt(match[1], 10);
    const year = parseInt(match[2], 10);

    return dayjs().year(year).isoWeek(week).startOf("isoWeek");
  };

  return Object.values(grouped)
    .map((entries) => ({
      ...entries[0],
      quantity: entries.reduce((sum, entry) => sum + entry.quantity, 0),
    }))
    .sort((e1, e2) => {
      if (e1.cwDate === undefined || e2.cwDate === undefined) {
        return -1;
      }
      const dateA = parseDate(e1.cwDate);
      const dateB = parseDate(e2.cwDate);
      return dateA.valueOf() - dateB.valueOf();
    });
}
